import { STEP_STATE_CHANGE_EVENT } from './shared/step-state.js';
import {
	DATASET_LISTENER_BOUND_KEY,
	DATASET_STATE_LISTENER_BOUND_KEY,
	STEP_BUTTON_SELECTOR,
	STEP_TOGGLE_SELECTOR,
} from './sidebar/sidebar-constants.js';
import {
	applyPrimaryStepExpansion,
	handlePrimaryToggleClick,
} from './sidebar/sidebar-step-accordion.js';
import {
	syncPrimaryProgressBar,
} from './sidebar/sidebar-progress-sync.js';
import {
	registerSidebarPreSubmissionFilter,
	clearRequestedTargetPage,
	handleStepNavigationButtonClick,
} from './sidebar/sidebar-step-navigation.js';
import {
	buildStepsRenderSignature,
	renderStepsList,
} from './sidebar/sidebar-step-render.js';
import {
	buildSidebarViewModel,
	commitSidebarViewModelRuntime,
	registerConditionalPageActionListeners,
} from './sidebar/sidebar-step-state.js';
import {
	readSidebarRenderSignature,
	readSidebarSyncFrameId,
	rememberSidebarApp,
	resetSidebarRuntime,
	writeSidebarRenderSignature,
	writeSidebarSyncFrameId,
} from './sidebar/sidebar-runtime-store.js';
import {
	getForm,
	getStepsListElement,
	toPositiveInteger,
} from './sidebar/sidebar-utils.js';

/**
* Responsibility:
* - Synchronize sidebar step markup against the latest explicit sidebar view model.
*
* Touches:
* - Resolves grouped primary-step state, clears pending requested targets, and syncs the grouped progress bar.
*
* Guarantees:
* - Sidebar rendering stays aligned with grouped current/complete/attention truth from one resolved view model.
*/
function syncSidebarSteps(app) {
	const form = getForm(app.config);
	if (!(form instanceof HTMLElement)) {
		return;
	}

	const listElement = getStepsListElement();
	if (!(listElement instanceof HTMLElement)) {
		app.logger.debug('Sidebar step bindings skipped: sidebar mount is missing.', {
			formId: app.config.formId,
		});
		return;
	}

	bindStepsClickListener(listElement, app);
	registerSidebarPreSubmissionFilter(app);

	const viewModel = buildSidebarViewModel(app, form);
	commitSidebarViewModelRuntime(viewModel);
	const previousRenderSignature = readSidebarRenderSignature(viewModel.formId);
	if (viewModel.primaryStepStates.length <= 0) {
		if (previousRenderSignature !== '' || listElement.childElementCount > 0) {
			listElement.innerHTML = '';
			writeSidebarRenderSignature(viewModel.formId, '');
		}
		syncPrimaryProgressBar(app, []);
		clearRequestedTargetPage(form);
		return;
	}

	const nextRenderSignature = buildStepsRenderSignature(viewModel);
	if (nextRenderSignature !== previousRenderSignature) {
		renderStepsList(listElement, viewModel);
		writeSidebarRenderSignature(viewModel.formId, nextRenderSignature);
		applyPrimaryStepExpansion(listElement, viewModel.formId, viewModel.expandedTargetPage, {
			animate: false,
		});
	}

	syncPrimaryProgressBar(app, viewModel.primaryStepStates);
	clearRequestedTargetPage(form);

	app.logger.debug('Sidebar step navigation synchronized.', {
		formId: viewModel.formId,
		currentPage: viewModel.currentPage,
		maxVisitedPage: viewModel.maxVisitedPage,
		stepCount: viewModel.primaryStepStates.length,
		attentionStepCount: viewModel.primaryStepStates.filter((stepState) => stepState.attention === true).length,
	});
}

/**
* Responsibility:
* - Queue one sidebar re-render after async step-state changes settle in the current frame.
*
* Touches:
* - Uses module-level runtime state to collapse repeated re-render triggers per form id.
*
* Guarantees:
* - Executes at most one sidebar synchronization per animation frame for the current configurator form.
*/
function queueSidebarSync(app) {
	const formId = toPositiveInteger(app?.config?.formId);
	if (formId <= 0) {
		return;
	}

	const activeFrameId = readSidebarSyncFrameId(formId);
	if (activeFrameId > 0) {
		window.cancelAnimationFrame(activeFrameId);
	}

	const nextFrameId = window.requestAnimationFrame(() => {
		writeSidebarSyncFrameId(formId, 0);
		syncSidebarSteps(app);
	});

	writeSidebarSyncFrameId(formId, nextFrameId);
}

/**
* Responsibility:
* - Listen for shared async step-state changes that occur without a GF page lifecycle event.
*
* Touches:
* - Attaches one bubbling `STEP_STATE_CHANGE_EVENT` listener to the current form.
*
* Guarantees:
* - Sidebar is re-rendered when advice, TCO, or ROI status changes on the current page.
*/
function bindStepStateChangeListener(form, app) {
	if (!(form instanceof HTMLElement)) {
		return;
	}

	if (form.dataset[DATASET_STATE_LISTENER_BOUND_KEY] === '1') {
		return;
	}

	form.addEventListener(STEP_STATE_CHANGE_EVENT, (event) => {
		const detail = event && typeof event === 'object' && 'detail' in event ? event.detail : null;
		const detailObject = detail && typeof detail === 'object' ? detail : {};
		const eventFormId = toPositiveInteger(detailObject.formId);
		if (eventFormId > 0 && eventFormId !== toPositiveInteger(app.config.formId)) {
			return;
		}

		queueSidebarSync(app);
	});

	form.dataset[DATASET_STATE_LISTENER_BOUND_KEY] = '1';
}

/**
* Responsibility:
* - Register one delegated click listener for sidebar navigation and accordion interactions.
*
* Touches:
* - Attaches a single listener to the steps list mount and dispatches to the focused navigation/accordion modules.
*
* Guarantees:
* - Listener is attached at most once per rendered sidebar list element.
*/
function bindStepsClickListener(listElement, app) {
	if (!(listElement instanceof HTMLElement)) {
		return;
	}

	if (listElement.dataset[DATASET_LISTENER_BOUND_KEY] === '1') {
		return;
	}

	listElement.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}

		const toggleButton = target.closest(STEP_TOGGLE_SELECTOR);
		if (toggleButton instanceof HTMLButtonElement) {
			handlePrimaryToggleClick(app.config.formId, listElement, event, toggleButton);
			return;
		}

		const button = target.closest(STEP_BUTTON_SELECTOR);
		if (button instanceof HTMLButtonElement) {
			handleStepNavigationButtonClick(app, event, button);
		}
	});

	listElement.dataset[DATASET_LISTENER_BOUND_KEY] = '1';
}

/**
* Responsibility:
* - Bind sidebar steps rendering and navigation for the configurator form.
*
* Touches:
* - Resolves form/list mounts, updates step classes, and syncs queue state per GF render cycle.
*
* Guarantees:
* - Sidebar list always reflects current/complete/attention/locked state from the resolved sidebar view model.
*/
export function bindSidebarStepsNav(app) {
	const form = getForm(app.config);
	if (!(form instanceof HTMLElement)) {
		return;
	}

	rememberSidebarApp(app);
	registerConditionalPageActionListeners(app);
	bindStepStateChangeListener(form, app);
	registerSidebarPreSubmissionFilter(app);
	syncSidebarSteps(app);
}

/**
* Responsibility:
* - Tear down transient sidebar runtime state before the current configurator page is removed.
*
* Touches:
* - Cancels queued sync frames and drops runtime state for the active form id.
*
* Guarantees:
* - Later page visits start with a clean sidebar runtime store.
*/
export function destroySidebarStepsNav(appOrFormId = 0) {
	const formId = typeof appOrFormId === 'object'
		? toPositiveInteger(appOrFormId?.config?.formId)
		: toPositiveInteger(appOrFormId);

	resetSidebarRuntime(formId);
}
